import React, {useState} from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, cellText} from '../schoolData';
import {NAVY, PageHeader, headCell, bodyCell} from '../components/widgets.comp';
import {EvidenceCell, RecommendationCell} from '../../DashboardCommon/RecommendationCells.comp';
import {isScopeSuppressed} from '../../DashboardCommon/dashboardCards';
import {FAMILY_CODE_BY_NAME} from '../../DashboardCommon/priorityFamilies';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Rows for groups under the n floor are not built by the datahouse (D-32): the empty state says why. */
const SUPPRESSED_EMPTY = 'Priority actions are not shown for groups of fewer than 10 students.';
const NO_MATCH = 'No row matches the filters.';

/* Slide 36 — Recommended System Interventions. Same Target Group rows and
   blank-cell convention as the Learning Program page (slide 34) — see that
   file's doc comment. No button at all on this slide (no Take Action, no
   "View Full List" — the deck has zero button shapes here). "Full list
   will be shown." and "Evidence & Recommendation logic is stored in a
   Google Doc." are yellow annotation boxes (instructions for the build),
   not literal on-page copy, so neither is rendered.
   "Standard action families" come from the card (see below). Table styling matches this dashboard's own
   headCell/bodyCell convention, not the mockup's own colors. */
/* Live rows: SD-36-01 → SD.S_21.C_2 (PriorityTableCard). The card's
   Target Group, Baseline Evidence and Recommended Actions columns fill this table as
   laid out on the slide; the card's extra Priority column is not on the slide.
   Every column sorts from its header; the Target Group filters once there are
   enough rows (QA 9 Oct 2026). */
/* "Standard Action Families" are the families the card itself lists
   (filters.action_family) — only those that apply to this school's recommendations.
   Selecting one filters the table to the recommendations in that family: each
   row carries its family codes (row_meta.families, P1 Data Map SD-36-01 /
   SND-35-02), named as in the Priority Actions guide (Methodology → How
   Priority Actions Are Triggered). Nothing selected shows every row. The
   name → code map is DashboardCommon/priorityFamilies. */
const FamilyFilterChip = ({label, active, onClick}) => (
    <Box
        onClick={onClick}
        sx={{
            cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap',
            px: 1.75, py: 0.75, borderRadius: '8px',
            border: `1px solid ${active ? NAVY : 'var(--brand-border, #E5E7EB)'}`,
            backgroundColor: active ? NAVY : 'var(--brand-surface, #ffffff)',
            color: active ? '#ffffff' : NAVY,
            '&:hover': {borderColor: NAVY},
        }}
    >
        {label}
    </Box>
);

const COLUMNS = [
    {key: 'targetGroup', label: 'Target Group', type: 'text'},
    // The slide renames the card's Baseline Evidence column (Ian, 1 Oct). Both list cells sort by their text; no checklist.
    {key: 'evidence', label: 'Areas of Weakness', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.evidence)},
    {key: 'recommendation', label: 'Recommended Actions', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.recommendation)},
];

const RecommendationTable = ({rows, emptyText = 'No system intervention recommendations yet.'}) => {
    const t = useTableControls(rows, COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: 'left', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.targetGroup}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.targetGroup}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><EvidenceCell value={row._raw?.evidence} fallback={row.evidence}/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><RecommendationCell row={row}/></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PRIORITY_ACTIONS_SI_SECTION_CODE = 'S_21';
const CARD_SECTIONS = [PRIORITY_ACTIONS_SI_SECTION_CODE];

const PriorityActionsSystemInterventionPage = () => {
    const [activeFamily, setActiveFamily] = useState(null);
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const cardForFamilies = findCard(dashboardData, PRIORITY_ACTIONS_SI_SECTION_CODE, 'C_2');
    const ACTION_FAMILIES = cardForFamilies?.filters?.action_family || [];
    const card = findCard(dashboardData, PRIORITY_ACTIONS_SI_SECTION_CODE, 'C_2');
    const rows = rowsFrom(card, {targetGroup: 'Target Group', evidence: 'Baseline Evidence', recommendation: 'Recommended Actions'})
        .map((r, i) => ({...r, families: card?.row_meta?.[i]?.families || []}));
    const familyCode = activeFamily ? FAMILY_CODE_BY_NAME[activeFamily] : null;
    const shown = familyCode ? rows.filter((r) => r.families.includes(familyCode)) : rows;

    return (
        <SchoolDashboardShell>
            <PageHeader
                title="Recommended System Interventions"
                subtitle="The crosswalk converts priority COS areas into standard action families, concrete interventions, and deployment status."
            />

            {ACTION_FAMILIES.length > 0 && (
            <>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                Standard Action Families
            </Typography>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 3}}>
                {ACTION_FAMILIES.map((f) => (
                    <FamilyFilterChip key={f} label={f} active={activeFamily === f} onClick={() => setActiveFamily(activeFamily === f ? null : f)}/>
                ))}
            </Box>
            </>
            )}

            <RecommendationTable rows={shown} emptyText={rows.length ? NO_MATCH : (isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : undefined)}/>
        </SchoolDashboardShell>
    );
};

export default PriorityActionsSystemInterventionPage;
